import React, { BaseSyntheticEvent, useState } from "react";

import { MdmObjectDto, MdmObjectNoteDto, useCreateNoteMutation, useDeleteNoteMutation, useGetAllNotesQuery, useUpdateNoteMutation } from "gql/generated/types";
import { GET_NOTES } from "gql/note.operations";
import { Button } from "primereact/button";
import { Card } from 'primereact/card';
import { confirmPopup } from "primereact/confirmpopup";
import { Editor } from "primereact/editor";
import { ProgressSpinner } from "primereact/progressspinner";
import { DEFAULT_DATE_FORMATTER } from "utils/constants";
import { parseToLocalZoneDateTime } from "utils/time-utils";
import { errorToaster, successToaster } from "../utils/utils";

type ObjectNotesProps = {
  object: MdmObjectDto
}

const ObjectNotes: React.FC<ObjectNotesProps> = ({ object }) => {
  const [note, setNote] = useState<string | null>(null);
  const [noteData, setNoteData] = useState<MdmObjectNoteDto | null>(null);
  const [editable, setEditable] = useState(false);
  
  const [createNoteMutation, { loading: loadingCreate }] = useCreateNoteMutation({
    onCompleted: () => successToaster("Note created successfully"),
    onError: errorToaster,
  });

  const [updateNoteMutation, { loading: loadingUpdate }] = useUpdateNoteMutation({
    onCompleted: () => {
      successToaster("Note updated successfully");
      cancelEdit();
    },
    onError: errorToaster,
  });

  const {data, loading} = useGetAllNotesQuery({
    variables: {
      id: object.id!
    },
    skip: !object.id
  })

  const handleNoteSend = () => {
    if (!note) return;
    createNoteMutation({
      variables: {
        objectId: object.id!,
        note
      },
      update: (cache, { data: qData }) => {
        cache.updateQuery({ query: GET_NOTES, variables: { id: object.id } }, oldData =>
          oldData
            ? {
                allNotesByObjectId: [...oldData.allNotesByObjectId, qData?.createNote],
              }
            : null
        );
      }
    })
    setNote(null)
  }

  const handleEditClick = (objectNote: MdmObjectNoteDto) => {
    setNote(objectNote.note!);
    setNoteData(objectNote);
    setEditable(true);
  }

  const handleNoteUpdate = () => {
    const {createdByUsername, modifiedByUsername, ...restNote} = noteData!;
    updateNoteMutation({ variables: { note: { ...restNote, note } } });
  }
  
  const cancelEdit = () => {
    setNote(null);
    setEditable(false);
    setNoteData(null);
  }
  const notesResponse = data?.allNotesByObjectId ?? [];
  const mapNotes = [...notesResponse]
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .map(n => <ObjectNote key={n.id} note={n} onEditNote={handleEditClick} />);

  return (
    <div className="mx-auto" style={{ maxWidth: "800px" }}>
      {loading && <ProgressSpinner className="mx-auto w-full"/>}
      {data && data.allNotesByObjectId.length > 0 && (
        <Card style={{ maxHeight: "500px", overflowY: "auto" }}>{mapNotes}</Card>
      )}
      <Editor
        value={note ?? ""}
        onTextChange={e => setNote(e.htmlValue)}
        className="mt-3"
        style={{ minHeight: "100px" }}
      />
      {editable ? (
        <div className="flex flex-row gap-3 mt-3">
          <Button type="button" label="Update" onClick={handleNoteUpdate} loading={loadingUpdate} />
          <Button type="button" label="Cancel" className="p-button-secondary" onClick={cancelEdit} />
        </div>
      ) : (
        <Button type="button" label="Send" onClick={handleNoteSend} className="w-auto mt-3" loading={loadingCreate} />
      )}
    </div>
  );
}

export default ObjectNotes;

type ObjectNoteProps = {
  note: MdmObjectNoteDto;
  onEditNote: (note: MdmObjectNoteDto) => void;
};

const ObjectNote: React.FC<ObjectNoteProps> = ({ note, onEditNote }) => {
  const [deleteNoteMutation] = useDeleteNoteMutation({
    onCompleted: () => successToaster("Note deleted successfully"),
    onError: errorToaster,
  });

  const confirmOnDelete = (event: BaseSyntheticEvent) => {
    confirmPopup({
      target: event.currentTarget,
      message: "Are you sure you want to delete this note?",
      acceptClassName: "p-button-danger",
      icon: "pi pi-exclamation-triangle",
      accept: () =>
        deleteNoteMutation({
          variables: {
            id: note.id,
          },
          update(cache) {
            const normalizedId = cache.identify({ id: note.id, __typename: "MdmObjectNoteDTO" });
            cache.evict({ id: normalizedId });
            cache.gc();
          },
        }),
    });
  };
  const updated = Math.abs(new Date(note.createdAt).getTime() - new Date(note.modifiedAt).getTime()) > 10;

  return (
    <div id="note" className="mb-3 border-1 border-300 border-round px-3 pb-2">
      <div className="flex justify-content-end align-items-center m-0">
        <span className="text-500">{note.createdByUsername}</span>
        <span className="text-500 ml-5 mr-auto">
          {parseToLocalZoneDateTime(note.createdAt).format(DEFAULT_DATE_FORMATTER)}
          {updated && (
            <span
              title={`Edited on ${parseToLocalZoneDateTime(note.modifiedAt).format(DEFAULT_DATE_FORMATTER)}, by ${note.modifiedByUsername}`}
            >
              &nbsp;(edited)
            </span>
          )}
        </span>
        <Button
          type="button"
          icon="pi pi-pencil"
          title="Edit"
          className="p-button-rounded p-button-text p-button-info p-0"
          onClick={() => onEditNote(note)}
        />
        <Button
          type="button"
          icon="pi pi-trash"
          title="Delete"
          className="p-button-rounded p-button-text p-button-danger p-0"
          onClick={confirmOnDelete}
        />
      </div>
      <div
        dangerouslySetInnerHTML={{ __html: note.note ?? "" }}
        style={{ wordBreak: "break-word" }}
      />
    </div>
  );
};